import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Button,
  Divider,
  Collapse,
  ListItemText,
  TextField,
  useTheme,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
// Ícones para Navbar e itens fixos
import MenuIcon from '@mui/icons-material/Menu';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';
import LayersIcon from '@mui/icons-material/Layers';
import ViewStreamIcon from '@mui/icons-material/ViewStream';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import LogoutIcon from '@mui/icons-material/Logout';
import ColorLensIcon from '@mui/icons-material/ColorLens';
import LockResetIcon from '@mui/icons-material/LockReset';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import PersonIcon from '@mui/icons-material/Person';
import PriceCheckIcon from '@mui/icons-material/PriceCheck';

// Ícones para os itens dinâmicos (mapeamento dos ícones do Material UI)
import BadgeIcon from '@mui/icons-material/Badge';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import CategoryIcon from '@mui/icons-material/Category';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import EditIcon from '@mui/icons-material/Edit';
import PieChartIcon from '@mui/icons-material/PieChart';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';

import { useAuth } from '../contexts/AuthContext';
import { useWindowManager } from '../contexts/WindowManagerContext';
import { useThemeConfig } from '../contexts/ThemeContext';
import ThemeModal from './ThemeModal';

import { encryptData, decryptData } from '../services/cryptoUtils';
import { completeSync, enviarPedidosSIC } from '../services/sync/completeDataSync';
import { useNotification } from '../contexts/NotificationContext';
import db from '../services/db';
// Mapeamento dos valores em mui_icon para os componentes do Material UI
const iconMapping = {
  "Badge": <BadgeIcon />,
  "Inventory2": <Inventory2Icon />,
  "Category": <CategoryIcon />,
  "AccountBalance": <AccountBalanceIcon />,
  "ShoppingCart": <ShoppingCartIcon />,
  "AddShoppingCart": <AddShoppingCartIcon />,
  "MonetizationOn": <MonetizationOnIcon />,
  "ReceiptLong": <ReceiptLongIcon />,
  "Edit": <EditIcon />,
  "PieChart": <PieChartIcon />,
  "ArrowDownward": <ArrowDownwardIcon />,
  "RadioButtonUnchecked-red": <RadioButtonUncheckedIcon sx={{ color: 'red' }} />,
  "RadioButtonUnchecked-blue": <RadioButtonUncheckedIcon sx={{ color: 'blue' }} />,
  "ShowChart": <ShowChartIcon />,
  "InsertDriveFile": <InsertDriveFileIcon />,
  "DirectionsBus": <DirectionsBusIcon />,
};

//
// Componente para renderizar recursivamente um item do menu dinâmico
//
const DynamicMenuItem = ({ option, level = 0, handleStartMenuClose }) => {
  const {
    windows,
    openWindow,
    minimizeWindow,
    restoreWindow,
    minimizeAllWindows,
    restoreAllWindows,
    updateWindowPosition,
    focusedWindow,
    setFocusedWindow,
  } = useWindowManager();
  const [open, setOpen] = useState(false);

  const handleToggle = () => {
    setOpen((prev) => !prev);
  };

  const handleOptionClick = () => {
    let url;
    if (option.telaReact) {
      url = option.telaReact;
    }

    openWindow(
      option.id + '-' + option.nome,
      option.id + '-' + option.nome,
      <iframe
        src={url}
        style={{ width: '100%', height: '100%', border: 'none' }}
        title={option.id + '-' + option.nome}
      />
    );
    handleStartMenuClose();
  };

  return (
    <>
      {option.opcaoFilhos && option.opcaoFilhos.length > 0 ? (
        <>
          <MenuItem onClick={handleToggle} sx={{ pl: level * 2 }}>
            {option.muiIcon && iconMapping[option.muiIcon] && (
              <Box sx={{ mr: 1 }}>{iconMapping[option.muiIcon]}</Box>
            )}
            <ListItemText primary={option.nome} />
            {open ? <ExpandLess /> : <ExpandMore />}
          </MenuItem>
          <Collapse in={open} timeout="auto" unmountOnExit>
            {option.opcaoFilhos.map((child, index) => (
              <DynamicMenuItem
                key={child.id || index}
                option={child}
                level={level + 1}
                handleStartMenuClose={handleStartMenuClose}
              />
            ))}
          </Collapse>
        </>
      ) : (
        <MenuItem onClick={handleOptionClick} sx={{ pl: level * 2 }}>
          {option.muiIcon && iconMapping[option.muiIcon] && (
            <Box sx={{ mr: 1 }}>{iconMapping[option.muiIcon]}</Box>
          )}
          <ListItemText primary={`${option.id} - ${option.nome}`} />
        </MenuItem>
      )}
    </>
  );
};

//
// Função recursiva para filtrar as opções de menu com base no texto digitado
//
const filterMenuOptions = (options, filter) => {
  if (!filter) return options;
  return options
    .map((option) => {
      const filteredChildren = option.opcaoFilhos
        ? filterMenuOptions(option.opcaoFilhos, filter)
        : [];
      if (
        option.nome.toLowerCase().includes(filter.toLowerCase()) ||
        filteredChildren.length > 0
      ) {
        return { ...option, opcaoFilhos: filteredChildren };
      }
      return null;
    })
    .filter((option) => option !== null);
};

export default function Navbar() {
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  const theme = useTheme();
  const { user, logout, opcaoMenu } = useAuth();
  const {
    windows,
    openWindow,
    minimizeWindow,
    restoreWindow,
    minimizeAllWindows,
    restoreAllWindows,
    updateWindowPosition,
    focusedWindow,
    setFocusedWindow,
  } = useWindowManager();
  const { themeSettings } = useThemeConfig();

  const navbarPosition = themeSettings.navbarPosition || 'bottom';
  const [themeModalOpen, setThemeModalOpen] = useState(false);
  const [startMenuAnchor, setStartMenuAnchor] = useState(null);
  const [filterText, setFilterText] = useState("");
  const filterRef = useRef(null);
  const [syncConfirmOpen, setSyncConfirmOpen] = useState(false);
  const [confirmCountdown, setConfirmCountdown] = useState(5); // timer de 5 segundos
  const [lockScreen, setLockScreen] = useState(false);
  // Reinicia e inicia o timer ao abrir o diálogo de sincronização
  useEffect(() => {
    if (syncConfirmOpen) {
      setConfirmCountdown(5);
      const timer = setInterval(() => {
        setConfirmCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [syncConfirmOpen]);

  // Reestabelece o foco no campo de filtro sempre que o menu estiver aberto e o valor mudar
  useEffect(() => {
    if (startMenuAnchor && filterRef.current) {
      filterRef.current.focus();
    }
  }, [filterText, startMenuAnchor]);

  const handleOpenThemeModal = () => {
    setThemeModalOpen(true);
    handleStartMenuClose();
  };
  const handleCloseThemeModal = () => {
    setThemeModalOpen(false);
  };

  const handleStartButtonClick = (event) => {
    setStartMenuAnchor(event.currentTarget);
  };
  const handleStartMenuClose = () => {
    setStartMenuAnchor(null);
  };

  // Funções de abertura de janelas (regra antiga)
  const openClientesWindow = () => {
    openWindow(
      'clientes',
      'Clientes',
      <iframe
        src="/clientes"
        style={{ width: '100%', height: '100%', border: 'none' }}
        title="Clientes"
      />
    );
    handleStartMenuClose();
  };
  const openPedidoVendaWindow = () => {
    openWindow(
      'pedidoVenda',
      'Pedido de Venda',
      <iframe
        src="/pedidos"
        style={{ width: '100%', height: '100%', border: 'none' }}
        title="Pedido de Venda"
      />
    );
    handleStartMenuClose();
  };
  const openListaPrecoWindow = () => {
    openWindow(
      'listaPreco',
      'Lista de Preços',
      <iframe
        src="/listaPreco"
        style={{ width: '100%', height: '100%', border: 'none' }}
        title="Lista de Preços"
      />
    );
    handleStartMenuClose();
  };
  const openAlterarSenhaWindow = () => {
    openWindow(
      'alterarSenha',
      'Alterar Senha',
      <iframe
        src="/alterarSenha"
        style={{ width: '100%', height: '100%', border: 'none' }}
        title="Alterar Senha"
      />
    );
    handleStartMenuClose();
  };

  // Antes de sincronizar, exibe a confirmação
  const handleSincronizar = () => {
    setSyncConfirmOpen(true);
    handleStartMenuClose();
  };

  const handleSyncConfirm = async  () => {
     try {
    console.log('aqe3')
    const pedidosPDM = await db.pedidos
      .filter(p => p.descricaoSerie === 'PDM')
      .toArray();

    if (pedidosPDM.length) {
      // 2. Ordena por idLocal (pode trocar por outro campo)
      const ordenados = pedidosPDM.sort((a, b) => a.idLocal - b.idLocal);

      // 3. Prepara a lista com descricaoSerie alterado para 'PD'
      const atualizados = ordenados.map(p => ({
        ...p,
        descricaoSerie: 'MB'
      }));

      // 4. Grava em lote no IndexedDB
      await db.pedidos.bulkPut(atualizados);
    }
  } catch (err) {
    console.error('Erro ao atualizar pedidos PDM:', err);
    showNotification('Erro ao atualizar pedidos com série PDM.', 'error');
    // você pode optar por abortar ou continuar mesmo assim
  }



    enviarPedidosSIC(user);
    setSyncConfirmOpen(false);
    // Inicia o bloqueio da tela por 10 segundos
    setLockScreen(true);
    setTimeout(() => {
      setLockScreen(false);
    }, 10000); // 10000 milissegundos = 10 segundos
  };

  const handleSyncCancel = () => {
    setSyncConfirmOpen(false);
  };

  // Funções de organização de janelas (regra antiga)
  const arrangeCascade = () => {
    const availableWidth = window.innerWidth;
    const availableHeight = window.innerHeight - 50;
    const offset = 30;
    windows.forEach((win, i) => {
      win.isMaximized = false;
      const newX = offset * i;
      const newY = offset * i;
      const newWidth = availableWidth - newX;
      const newHeight = availableHeight - newY;
      updateWindowPosition(
        win.id,
        { x: newX, y: newY, width: newWidth, height: newHeight },
        { skipAutoMaximize: true }
      );
    });
    handleStartMenuClose();
  };

  const arrangeStacked = () => {
    const availableWidth = window.innerWidth;
    const availableHeight = window.innerHeight - 50;
    const windowHeight = availableHeight / windows.length;
    windows.forEach((win, i) => {
      win.isMaximized = false;
      updateWindowPosition(
        win.id,
        {
          x: 0,
          y: i * windowHeight,
          width: availableWidth,
          height: windowHeight,
        },
        { skipAutoMaximize: true }
      );
    });
    handleStartMenuClose();
  };

  const arrangeSideBySide = () => {
    const availableWidth = window.innerWidth;
    const availableHeight = window.innerHeight - 50;
    const windowWidth = availableWidth / windows.length;
    windows.forEach((win, i) => {
      win.isMaximized = false;
      updateWindowPosition(
        win.id,
        {
          x: i * windowWidth,
          y: 0,
          width: windowWidth,
          height: availableHeight,
        },
        { skipAutoMaximize: true }
      );
    });
    handleStartMenuClose();
  };

  // Clique na taskbar
  const handleTaskClick = (win) => {
    if (win.isMinimized) {
      restoreWindow(win.id);
      setFocusedWindow(win.id);
    } else if (focusedWindow === win.id) {
      minimizeWindow(win.id);
    } else {
      restoreWindow(win.id);
      setFocusedWindow(win.id);
    }
  };

  const toggleAllWindows = () => {
    if (windows.every((w) => w.isMinimized)) {
      restoreAllWindows();
    } else {
      minimizeAllWindows();
    }
  };

  const handleLogout = () => {
    logout();
    handleStartMenuClose();
  };

  const handleTrocarEmpresa = () => {
    navigate('/selecaoMatriz');
  };

  // Para usuários sic===true, usamos o menu dinâmico (com árvore de pais, filhos e netos)
  const dynamicMenuOptions =
    user && decryptData(opcaoMenu) && decryptData(opcaoMenu).length > 0
      ? decryptData(opcaoMenu)
      : [];
  const filteredMenuOptions = filterMenuOptions(dynamicMenuOptions, filterText);

  return (
    <Box
      component="header"
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        width: '100%',
        height: 60,
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        display: 'flex',
        alignItems: 'center',
        zIndex: 1300,
        px: 1,
        borderTop: `1px solid ${theme.palette.divider}`,
        top: navbarPosition === 'top' ? 0 : 'auto',
        bottom: navbarPosition === 'bottom' ? 0 : 'auto',
        ...(navbarPosition === 'top'
          ? { borderBottom: `1px solid ${theme.palette.divider}` }
          : { borderTop: `1px solid ${theme.palette.divider}` }),
      }}
    >
      <IconButton onClick={handleStartButtonClick} color="inherit" sx={{ mr: 1 }}>
        <MenuIcon />
      </IconButton>
      <Menu
        anchorEl={startMenuAnchor}
        open={Boolean(startMenuAnchor)}
        onClose={handleStartMenuClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{
          sx: {
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            p: 1,
            maxWidth: '100vw',
            maxHeight: '100vh',
            overflow: 'auto',
          },
        }}
      >
        {user && user.sic ? (
          <Box>
            <Box sx={{ display: 'flex', p: 1 }}>
              {/* Coluna da esquerda: Menu dinâmico */}
              <Box sx={{ flex: 1, pr: 1, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ flex: 1, overflowY: 'auto', mb: 1 }}>
                  {filteredMenuOptions.length > 0 ? (
                    filteredMenuOptions.map((option, index) => (
                      <DynamicMenuItem
                        key={option.id || index}
                        option={option}
                        handleStartMenuClose={handleStartMenuClose}
                      />
                    ))
                  ) : (
                    <MenuItem disabled>Nenhuma opção encontrada</MenuItem>
                  )}
                </Box>
              </Box>
              {/* Coluna da direita: Opções de organização e globais */}
              <Box sx={{ width: 250, pl: 1 }}>
                <MenuItem onClick={arrangeCascade}>
                  <LayersIcon fontSize="small" sx={{ mr: 1 }} />
                  Cascata
                </MenuItem>
                <MenuItem onClick={arrangeStacked}>
                  <ViewStreamIcon fontSize="small" sx={{ mr: 1 }} />
                  Empilhadas
                </MenuItem>
                <MenuItem onClick={arrangeSideBySide}>
                  <ViewColumnIcon fontSize="small" sx={{ mr: 1 }} />
                  Lado a Lado
                </MenuItem>
                <Divider sx={{ my: 1, borderBottomWidth: '2px' }} />
                <MenuItem onClick={handleOpenThemeModal}>
                  <ColorLensIcon fontSize="small" sx={{ mr: 1 }} />
                  Alterar Tema
                </MenuItem>
                <MenuItem onClick={openAlterarSenhaWindow}>
                  <LockResetIcon fontSize="small" sx={{ mr: 1 }} />
                  Alterar Senha
                </MenuItem>
                <Divider sx={{ my: 1, borderBottomWidth: '2px' }} />
                <MenuItem onClick={handleLogout}>
                  <LogoutIcon fontSize="small" sx={{ mr: 1 }} />
                  Logout
                </MenuItem>
                <MenuItem onClick={handleTrocarEmpresa}>
                  <LogoutIcon fontSize="small" sx={{ mr: 1 }} />
                  Trocar de Empresa
                </MenuItem>
              </Box>
            </Box>
            <TextField
              label="Filtrar tela"
              variant="outlined"
              size="small"
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              fullWidth
              inputRef={filterRef}
              autoFocus
            />
          </Box>
        ) : (
          // Para sic===false: menu fixo com duas colunas (telas fixas à esquerda)
          <Box sx={{ display: 'flex' }}>
            <Box
              sx={{
                borderRight: `2px solid ${theme.palette.divider}`,
                pr: 2,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <MenuItem onClick={openClientesWindow}>
                <PersonIcon fontSize="small" sx={{ mr: 1 }} />
                Clientes
              </MenuItem>
              <MenuItem onClick={openPedidoVendaWindow}>
                <ShoppingCartIcon fontSize="small" sx={{ mr: 1 }} />
                Pedido de Venda
              </MenuItem>
              <MenuItem onClick={openListaPrecoWindow}>
                <PriceCheckIcon fontSize="small" sx={{ mr: 1 }} />
                Lista de Preços
              </MenuItem>
            </Box>
            <Box
              sx={{
                pl: 2,
                display: 'flex',
                flexDirection: 'column',
                width: '250px',
              }}
            >
              <MenuItem onClick={arrangeCascade}>
                <LayersIcon fontSize="small" sx={{ mr: 1 }} />
                Cascata
              </MenuItem>
              <MenuItem onClick={arrangeStacked}>
                <ViewStreamIcon fontSize="small" sx={{ mr: 1 }} />
                Empilhadas
              </MenuItem>
              <MenuItem onClick={arrangeSideBySide}>
                <ViewColumnIcon fontSize="small" sx={{ mr: 1 }} />
                Lado a Lado
              </MenuItem>
              <Divider sx={{ my: 1, borderBottomWidth: '2px' }} />
              <MenuItem onClick={handleOpenThemeModal}>
                <ColorLensIcon fontSize="small" sx={{ mr: 1 }} />
                Alterar Tema
              </MenuItem>
              <MenuItem onClick={openAlterarSenhaWindow}>
                <LockResetIcon fontSize="small" sx={{ mr: 1 }} />
                Alterar Senha
              </MenuItem>
              <MenuItem onClick={handleSincronizar}>
                <LockResetIcon fontSize="small" sx={{ mr: 1 }} />
                Sincronizar APP
              </MenuItem>
              <Divider sx={{ my: 1, borderBottomWidth: '2px' }} />
              <MenuItem onClick={handleLogout}>
                <LogoutIcon fontSize="small" sx={{ mr: 1 }} />
                Logout
              </MenuItem>
            </Box>
          </Box>
        )}
      </Menu>
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          alignItems: 'center',
          overflowX: 'auto',
          gap: 1,
        }}
      >
        {windows.map((win) => (
          <Button
            key={win.id}
            variant={focusedWindow === win.id ? 'contained' : 'outlined'}
            onClick={() => handleTaskClick(win)}
            sx={{
              color:
                focusedWindow === win.id
                  ? theme.palette.primary.contrastText
                  : theme.palette.text.primary,
              backgroundColor:
                focusedWindow === win.id
                  ? theme.palette.primary.main
                  : 'transparent',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            {win.title || 'Sem Título'}
          </Button>
        ))}
      </Box>
      <IconButton onClick={toggleAllWindows} color="inherit" sx={{ ml: 1 }}>
        <DesktopWindowsIcon />
      </IconButton>
      {themeModalOpen && <ThemeModal onClose={handleCloseThemeModal} />}
      {/* Diálogo de confirmação para sincronizar o APP */}
      <Dialog open={syncConfirmOpen} onClose={handleSyncCancel}>
        <DialogTitle>Confirmar Sincronização</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Deseja sincronizar o aplicativo? <br/>  TODOS OS PEDIDOS ENVIADOS NÃO PODE SER EDITADO?.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSyncCancel}>Cancelar</Button>
          <Button onClick={handleSyncConfirm} autoFocus disabled={confirmCountdown > 0}>
            {confirmCountdown > 0 ? `Confirmar (${confirmCountdown})` : 'Confirmar'}
          </Button>
        </DialogActions>
      </Dialog>
      {lockScreen && (
  <Box
    sx={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      zIndex: 2000,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      color: '#fff',
    }}
  >
    <h1>Sincronizando... Aguarde 10 segundos</h1>
  </Box>
)}



    </Box>

    
  );
}
